import React, { useState, useEffect } from 'react';
import { getPaymentStatusColor } from '../../utils/statusBadge';
import {
  Box,
  Button,
  useToast,
  Text,
  HStack,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  InputGroup,
  InputLeftElement,
  Input,
  Container,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Flex,
  Icon,
  VStack,
  Grid,
  GridItem,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import WeeklyBillingPreview from './WeeklyBillingPreview';
import InvoicesAndBatches from './InvoicesAndBatches';
import InvoiceStatsStrip from './InvoiceStatsStrip';
import { 
  SearchIcon, 
  ChevronDownIcon, 
  AddIcon,
  DownloadIcon
} from '@chakra-ui/icons';
import { 
  FaDollarSign, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaCreditCard,
  FaCalendarAlt,
  FaReceipt,
  FaChartLine,
  FaClock,
  FaTimesCircle,
  FaUndo,
  FaFileInvoiceDollar,
} from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

// WARNING: This component should NEVER use mock/dummy/fake data
// All billing transactions must come from real payment processors
const BillingManagement = ({ initialTab } = {}) => {
  const [transactions, setTransactions] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  
  // Filtering and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  const { isOpen: isTransactionModalOpen, onOpen: onTransactionModalOpen, onClose: onTransactionModalClose } = useDisclosure();
  const { isOpen: isManualChargeModalOpen, onOpen: onManualChargeModalOpen, onClose: onManualChargeModalClose } = useDisclosure();
  
  const toast = useToast();

  // Manual charge form
  const [manualChargeForm, setManualChargeForm] = useState({
    lab_id: '',
    amount: 0,
    description: '',
    charge_date: new Date().toISOString().split('T')[0]
  });

  // Fetch actual billing transactions from database
  const fetchTransactions = async () => {
    try {
      // Query actual transactions table - replace with your real billing table structure
      const { data, error } = await supabase
        .from('billing_transactions') // This table needs to be created
        .select(`
          *,
          labs!inner(name)
        `)
        .order('transaction_date', { ascending: false });

      if (error) {
        // If billing_transactions table doesn't exist yet, return empty array
        if (error.code === '42P01') {
          console.log('Billing transactions table not created yet');
          return [];
        }
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  };

  // Fetch labs and real transactions
  const fetchBillingData = async () => {
    try {
      setLoading(true);
      
      // Fetch labs
      const { data: labsData, error: labsError } = await supabase
        .from('labs')
        .select('*')
        .order('name');

      if (labsError) throw labsError;
      
      setLabs(labsData || []);
      
      // Fetch real transactions from database
      const realTransactions = await fetchTransactions();
      setTransactions(realTransactions);
      
    } catch (error) {
      toast({
        title: 'Error fetching billing data',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = !searchQuery || 
      transaction.lab_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      transaction.stripe_transaction_id?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || transaction.status === statusFilter;
    const matchesType = typeFilter === 'all' || transaction.type === typeFilter;
    
    let matchesDate = true;
    if (dateRange !== 'all') {
      const transactionDate = new Date(transaction.transaction_date);
      const now = new Date();
      switch (dateRange) {
        case 'today':
          matchesDate = transactionDate.toDateString() === now.toDateString();
          break;
        case 'week':
          matchesDate = transactionDate > new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          matchesDate = transactionDate > new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          matchesDate = transactionDate > new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
      }
    }

    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

  // Get status color
  const getStatusColor = (status) => getPaymentStatusColor(status);

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'subscription': return 'blue';
      case 'one_time': return 'teal';
      case 'refund': return 'purple';
      case 'adjustment': return 'orange';
      default: return 'gray';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle manual charge
  const handleManualCharge = async (e) => {
    e.preventDefault();
    
    try {
      // TODO: Integrate with actual payment processor (Stripe, etc.)
      // This should create a real transaction, not add to local state
      
      const selectedLab = labs.find(lab => lab.id === manualChargeForm.lab_id);
      
      const newTransaction = {
        id: `manual_${Date.now()}`,
        lab_id: manualChargeForm.lab_id,
        lab_name: selectedLab?.name,
        amount: parseFloat(manualChargeForm.amount),
        status: 'completed',
        type: 'one_time',
        description: manualChargeForm.description,
        transaction_date: new Date(manualChargeForm.charge_date).toISOString(),
        stripe_transaction_id: `manual_${Math.random().toString(36).substring(7)}`,
        payment_method: 'manual',
        last_4: 'N/A'
      };
      
      setTransactions(prev => [newTransaction, ...prev]);
      
      toast({
        title: 'Manual charge created',
        description: `${formatCurrency(manualChargeForm.amount)} charged to ${selectedLab?.name}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setManualChargeForm({
        lab_id: '',
        amount: 0,
        description: '',
        charge_date: new Date().toISOString().split('T')[0]
      });
      
      onManualChargeModalClose();
      
    } catch (error) {
      toast({
        title: 'Error creating charge',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Calculate statistics
  const stats = {
    totalRevenue: filteredTransactions
      .filter(t => t.status === 'completed' && t.type !== 'refund')
      .reduce((sum, t) => sum + t.amount, 0),
    totalRefunds: filteredTransactions
      .filter(t => t.status === 'completed' && t.type === 'refund')
      .reduce((sum, t) => sum + t.amount, 0),
    pendingAmount: filteredTransactions
      .filter(t => t.status === 'pending')
      .reduce((sum, t) => sum + t.amount, 0),
    failedAmount: filteredTransactions
      .filter(t => t.status === 'failed')
      .reduce((sum, t) => sum + t.amount, 0)
  };

  useEffect(() => {
    fetchBillingData();
  }, []);

  return (
    <Container maxW="full" py={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center" gap={4} wrap="wrap">
          <HStack spacing={6} align="center">
            <Heading size="lg" color="gray.800">
              Billing & Transactions
            </Heading>
            <InvoiceStatsStrip />
          </HStack>
          <HStack spacing={4}>
            <Button
              leftIcon={<Icon as={FaFileInvoiceDollar} />}
              colorScheme="blue"
              variant="outline"
              size="sm"
              onClick={onManualChargeModalOpen}
            >
              Manual Charge
            </Button>
            <Button
              leftIcon={<Icon as={DownloadIcon} />}
              colorScheme="green"
              size="sm"
              onClick={() => {
                // Export functionality
                toast({
                  title: 'Export started',
                  description: 'Transaction report will be downloaded shortly',
                  status: 'info',
                  duration: 3000,
                  isClosable: true,
                });
              }}
            >
              Export Report
            </Button>
          </HStack>
        </Flex>

        <Tabs colorScheme="teal" variant="enclosed" isLazy defaultIndex={initialTab === 'invoices' ? 2 : 0}>
          <TabList>
            <Tab>Transactions</Tab>
            <Tab>Weekly Cycle</Tab>
            <Tab>Invoices</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={4}>
              <VStack spacing={6} align="stretch">

        {/* Statistics Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Card size="sm">
            <CardBody>
              <Stat>
                <StatLabel fontSize="xs">Total Revenue</StatLabel>
                <StatNumber fontSize="lg" color="green.600">{formatCurrency(stats.totalRevenue)}</StatNumber>
                <StatHelpText fontSize="xs">
                  <Icon as={FaChartLine} mr={1} color="green.600" />
                  Completed transactions
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card size="sm">
            <CardBody>
              <Stat>
                <StatLabel fontSize="xs">Pending Charges</StatLabel>
                <StatNumber fontSize="lg" color="yellow.600">{formatCurrency(stats.pendingAmount)}</StatNumber>
                <StatHelpText fontSize="xs">
                  <Icon as={FaClock} mr={1} color="yellow.600" />
                  Processing payments
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card size="sm">
            <CardBody>
              <Stat>
                <StatLabel fontSize="xs">Failed Charges</StatLabel>
                <StatNumber fontSize="lg" color="red.600">{formatCurrency(stats.failedAmount)}</StatNumber>
                <StatHelpText fontSize="xs">
                  <Icon as={FaTimesCircle} mr={1} color="red.600" />
                  Payment failures
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card size="sm">
            <CardBody>
              <Stat>
                <StatLabel fontSize="xs">Refunds Issued</StatLabel>
                <StatNumber fontSize="lg" color="purple.600">{formatCurrency(stats.totalRefunds)}</StatNumber>
                <StatHelpText fontSize="xs">
                  <Icon as={FaUndo} mr={1} color="purple.600" />
                  Customer refunds
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Filters */}
        <Card>
          <CardBody>
            <Grid templateColumns={{ base: "1fr", md: "2fr 1fr 1fr 1fr 1fr" }} gap={4}>
              <GridItem>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by lab name, description, or transaction ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </GridItem>
              
              <GridItem>
                <Select
                  size="sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  icon={<ChevronDownIcon />}
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </Select>
              </GridItem>

              <GridItem>
                <Select
                  size="sm"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  icon={<ChevronDownIcon />}
                >
                  <option value="all">All Types</option>
                  <option value="subscription">Subscription</option>
                  <option value="one_time">One-time</option>
                  <option value="refund">Refund</option>
                  <option value="adjustment">Adjustment</option>
                </Select>
              </GridItem>

              <GridItem>
                <Select
                  size="sm"
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  icon={<ChevronDownIcon />}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                </Select>
              </GridItem>
            </Grid>
          </CardBody>
        </Card>

        {/* Results Summary and Pagination */}
        <Flex justify="space-between" align="center">
          <Text color="gray.600" fontSize="sm">
            Showing {paginatedTransactions.length} of {filteredTransactions.length} transactions
            {filteredTransactions.length !== transactions.length && ` (filtered from ${transactions.length} total)`}
          </Text>
          <HStack spacing={2}>
            <Button
              size="xs"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <Text fontSize="xs">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="xs"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Next
            </Button>
          </HStack>
        </Flex>

        {/* Transactions Table */}
        <Card>
          <CardBody p={0}>
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontSize="xs" py={2}>Date</Th>
                    <Th fontSize="xs" py={2}>Laboratory</Th>
                    <Th fontSize="xs" py={2}>Description</Th>
                    <Th fontSize="xs" py={2}>Amount</Th>
                    <Th fontSize="xs" py={2}>Status</Th>
                    <Th fontSize="xs" py={2}>Type</Th>
                    <Th fontSize="xs" py={2}>Payment Method</Th>
                    <Th fontSize="xs" py={2}>Transaction ID</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={4}>
                        Loading transactions...
                      </Td>
                    </Tr>
                  ) : paginatedTransactions.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={8}>
                        <VStack spacing={3}>
                          <Icon as={FaFileInvoiceDollar} boxSize={8} color="gray.400" />
                          <Text color="gray.500" fontSize="md">
                            No billing transactions found
                          </Text>
                          <Text color="gray.400" fontSize="sm">
                            Transactions will appear here once payment processing is set up
                          </Text>
                        </VStack>
                      </Td>
                    </Tr>
                  ) : (
                    paginatedTransactions.map((transaction) => (
                      <Tr 
                        key={transaction.id} 
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          onTransactionModalOpen();
                        }}
                      >
                        <Td py={2} fontSize="xs">
                          {formatDate(transaction.transaction_date)}
                        </Td>
                        <Td py={2} fontSize="sm" fontWeight="medium">
                          {transaction.lab_name}
                        </Td>
                        <Td py={2} fontSize="xs" maxW="200px">
                          <Text noOfLines={1}>{transaction.description}</Text>
                        </Td>
                        <Td py={2} fontSize="sm" fontWeight="medium">
                          <Text color={transaction.type === 'refund' ? 'red.600' : 'green.600'}>
                            {transaction.type === 'refund' ? '-' : ''}{formatCurrency(transaction.amount)}
                          </Text>
                        </Td>
                        <Td py={2}>
                          <Badge colorScheme={getStatusColor(transaction.status)} size="sm">
                            {transaction.status}
                          </Badge>
                        </Td>
                        <Td py={2}>
                          <Badge colorScheme={getTypeColor(transaction.type)} size="sm">
                            {transaction.type}
                          </Badge>
                        </Td>
                        <Td py={2} fontSize="xs">
                          {transaction.payment_method === 'card' ? `•••• ${transaction.last_4}` : transaction.payment_method}
                        </Td>
                        <Td py={2} fontSize="xs" fontFamily="mono">
                          {transaction.stripe_transaction_id}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>

              </VStack>
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <WeeklyBillingPreview />
            </TabPanel>
            <TabPanel px={0} pt={4}>
              <InvoicesAndBatches />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Transaction Detail Modal */}
        <Modal isOpen={isTransactionModalOpen} onClose={onTransactionModalClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Transaction Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedTransaction && (
                <VStack spacing={4} align="stretch">
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Transaction ID</Text>
                      <Text fontSize="sm" fontFamily="mono">{selectedTransaction.stripe_transaction_id}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Date</Text>
                      <Text fontSize="sm">{formatDate(selectedTransaction.transaction_date)}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Laboratory</Text>
                      <Text fontSize="sm">{selectedTransaction.lab_name}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Amount</Text>
                      <Text fontSize="sm" fontWeight="bold" color={selectedTransaction.type === 'refund' ? 'red.600' : 'green.600'}>
                        {selectedTransaction.type === 'refund' ? '-' : ''}{formatCurrency(selectedTransaction.amount)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Status</Text>
                      <Badge colorScheme={getStatusColor(selectedTransaction.status)}>
                        {selectedTransaction.status}
                      </Badge>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">Type</Text>
                      <Badge colorScheme={getTypeColor(selectedTransaction.type)}>
                        {selectedTransaction.type}
                      </Badge>
                    </Box>
                  </SimpleGrid>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">Description</Text>
                    <Text fontSize="sm">{selectedTransaction.description}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">Payment Method</Text>
                    <Text fontSize="sm">
                      {selectedTransaction.payment_method === 'card' ? 
                        `Credit Card ending in ${selectedTransaction.last_4}` : 
                        selectedTransaction.payment_method
                      }
                    </Text>
                  </Box>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button onClick={onTransactionModalClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Manual Charge Modal */}
        <Modal isOpen={isManualChargeModalOpen} onClose={onManualChargeModalClose}>
          <ModalOverlay />
          <ModalContent>
            <form onSubmit={handleManualCharge}>
              <ModalHeader>Create Manual Charge</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Laboratory</FormLabel>
                    <Select
                      size="sm"
                      value={manualChargeForm.lab_id}
                      onChange={(e) => setManualChargeForm(prev => ({ ...prev, lab_id: e.target.value }))}
                      placeholder="Select laboratory"
                    >
                      {labs.map(lab => (
                        <option key={lab.id} value={lab.id}>{lab.name}</option>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Amount</FormLabel>
                    <NumberInput
                      size="sm"
                      value={manualChargeForm.amount}
                      onChange={(value) => setManualChargeForm(prev => ({ ...prev, amount: parseFloat(value) || 0 }))}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Description</FormLabel>
                    <Textarea
                      size="sm"
                      value={manualChargeForm.description}
                      onChange={(e) => setManualChargeForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Reason for manual charge"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Charge Date</FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      value={manualChargeForm.charge_date}
                      onChange={(e) => setManualChargeForm(prev => ({ ...prev, charge_date: e.target.value }))}
                    />
                  </FormControl>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button variant="ghost" mr={3} onClick={onManualChargeModalClose} size="sm">
                  Cancel
                </Button>
                <Button colorScheme="blue" type="submit" size="sm">
                  Create Charge
                </Button>
              </ModalFooter>
            </form>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default BillingManagement;
